<template>
    <div>
        <!-- 文件预览对话框 -->
        <el-dialog :title="previewTitle" :visible.sync="previewVisible" width="90%" top="5vh" @close="handleClose"
            :modal=canDownload>
            <div v-loading="previewLoading" class="preview-container">
                <!-- 图片预览 -->
                <div v-if="isImage" class="image-preview">
                    <div v-if="imageLoadError || previewError" class="image-error">
                        <el-alert :title="previewError || '图片加载失败'" type="error" show-icon>
                            <p>可能的原因：</p>
                            <ul>
                                <li>图片文件已损坏</li>
                                <li>不支持该图片格式</li>
                                <li>网络连接问题</li>
                                <li>跨域限制</li>
                            </ul>
                        </el-alert>
                        <div class="error-action">
                            <el-button type="primary" @click="retryPreview">
                                <i class="el-icon-refresh"></i>
                                重试预览
                            </el-button>
                            <el-button v-if="canDownload" type="primary" @click="handleDownload">
                                <i class="el-icon-download"></i>
                                下载文件
                            </el-button>
                        </div>
                    </div>
                    <div v-else class="image-content">
                        <!-- 图片缩放工具栏 -->
                        <div class="image-toolbar" v-if="!imageLoading && !imageLoadError && previewUrl">
                            <el-button-group>
                                <el-button size="mini" @click="zoomOut" :disabled="scale <= 0.2">
                                    <i class="el-icon-zoom-out"></i>
                                    缩小
                                </el-button>
                                <el-button size="mini" @click="resetZoom">
                                    {{ Math.round(scale * 100) }}%
                                </el-button>
                                <el-button size="mini" @click="zoomIn" :disabled="scale >= 3">
                                    <i class="el-icon-zoom-in"></i>
                                    放大
                                </el-button>
                                <el-button size="mini" @click="rotateLeft">
                                    <i class="el-icon-refresh-left"></i>
                                    左旋转
                                </el-button>
                                <el-button size="mini" @click="rotateRight">
                                    <i class="el-icon-refresh-right"></i>
                                    右旋转
                                </el-button>
                                <el-button size="mini" @click="resetRotation">
                                    <i class="el-icon-refresh"></i>
                                    重置
                                </el-button>
                                <el-button size="mini" @click="toggleFullscreen" v-if="!isFullscreen">
                                    <i class="el-icon-full-screen"></i>
                                    全屏
                                </el-button>
                                <el-button size="mini" @click="toggleFullscreen" v-else>
                                    <i class="el-icon-close"></i>
                                    退出全屏
                                </el-button>
                            </el-button-group>
                        </div>

                        <!-- 图片容器 -->
                        <div class="image-container" :class="{ 'fullscreen': isFullscreen }" @wheel="handleWheel"
                            @mousedown="startDrag" @mousemove="handleDrag" @mouseup="endDrag" @mouseleave="endDrag">
                            <img v-if="!imageLoading && !imageLoadError && previewUrl" :src="previewUrl" :alt="fileName"
                                class="preview-image" @error="handleImageError" @load="handleImageLoad"
                                :style="imageTransform" />
                        </div>

                        <div v-if="imageLoading" class="image-loading">
                            <i class="el-icon-loading"></i>
                            <p>图片加载中...</p>
                        </div>
                    </div>
                </div>

                <!-- PDF预览 -->
                <div v-else-if="isPdf" class="pdf-preview" :class="{ 'no-select': !canDownload }">
                    <div v-if="pdfLoadError" class="pdf-error">
                        <el-alert title="PDF加载失败" type="error" show-icon>
                            <p>可能的原因：</p>
                            <ul>
                                <li>PDF文件已损坏</li>
                                <li>浏览器不支持PDF预览</li>
                                <li>网络连接问题</li>
                            </ul>
                        </el-alert>
                        <div class="error-action">
                            <el-button type="primary" @click="retryPreview">
                                <i class="el-icon-refresh"></i>
                                重试预览
                            </el-button>
                            <el-button v-if="canDownload" type="primary" @click="handleDownload">
                                <i class="el-icon-download"></i>
                                下载文件
                            </el-button>
                        </div>
                    </div>
                    <div v-else class="pdf-content">
                        <div v-show="pdfLoading" class="pdf-loading">
                            <i class="el-icon-loading"></i>
                            <p>PDF加载中...</p>
                        </div>
                        <div v-if="!pdfLoading && previewUrl" class="pdf-iframe-container">
                            <iframe :src="previewUrl" width="100%" height="100%" frameborder="0" @load="handlePdfLoad"
                                @error="handlePdfError" class="pdf-viewer"></iframe>
                        </div>
                    </div>
                </div>

                <!-- Excel预览 -->
                <div v-else-if="isExcel" class="excel-preview">
                    <div class="excel-toolbar">
                        <el-button-group>
                            <el-button size="small" :type="currentSheetIndex === index ? 'primary' : ''"
                                v-for="(sheet, index) in excelSheets" :key="index" @click="switchSheet(index)">
                                {{ sheet.name }}
                            </el-button>
                        </el-button-group>
                        <div class="excel-info">
                            共 {{ excelSheets.length }} 个工作表
                        </div>
                    </div>

                    <div class="excel-table-container">
                        <el-table :data="currentSheetData" border stripe height="500px" style="width: 100%"
                            v-loading="excelLoading">
                            <el-table-column v-for="(header, index) in currentSheetHeaders" :key="index"
                                :prop="header.key" :label="header.title" min-width="120" show-overflow-tooltip>
                            </el-table-column>
                        </el-table>
                    </div>

                    <div class="excel-pagination" v-if="currentSheetData.length > 100">
                        <el-pagination small layout="prev, pager, next" :total="currentSheetData.length"
                            :page-size="100" :current-page="currentPage" @current-change="handlePageChange">
                        </el-pagination>
                    </div>
                </div>

                <!-- DOCX文档预览 -->
                <div v-else-if="isDocx" class="docx-preview">
                    <div v-if="docxContent" class="docx-content" v-html="docxContent"></div>
                    <div v-else-if="previewError" class="preview-error">
                        <el-alert :title="previewError" type="error" show-icon>
                        </el-alert>
                        <div class="error-action">
                            <el-button type="primary" @click="retryPreview">
                                <i class="el-icon-refresh"></i>
                                重试预览
                            </el-button>
                            <el-button v-if="canDownload" type="primary" @click="handleDownload">
                                <i class="el-icon-download"></i>
                                下载文件
                            </el-button>
                        </div>
                    </div>
                </div>

                <!-- 文本文件预览 -->
                <div v-else-if="isText" class="text-preview">
                    <pre>{{ fileContent }}</pre>
                </div>

                <!-- 不支持预览的文件类型 -->
                <div v-else class="unsupported-preview">
                    <el-alert title="该文件类型不支持预览" :description="`文件类型: ${fileType}`" type="warning" show-icon>
                    </el-alert>
                    <div class="download-action">
                        <el-button v-if="canDownload" type="primary" @click="handleDownload">
                            <i class="el-icon-download"></i>
                            下载文件
                        </el-button>
                    </div>
                </div>
            </div>

            <span slot="footer" class="dialog-footer">
                <el-button @click="previewVisible = false">取消</el-button>
                <el-button v-if="canDownload" type="primary" @click="handleDownload">
                    下载文件
                </el-button>
            </span>
        </el-dialog>
    </div>
</template>
<script>
// 使用CDN方式引入库
function loadScript(src) {
    return new Promise((resolve, reject) => {
        if (window.mammoth && src.includes('mammoth')) {
            resolve(window.mammoth);
            return;
        }
        if (window.XLSX && src.includes('xlsx')) {
            resolve(window.XLSX);
            return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.onload = () => {
            if (src.includes('mammoth')) resolve(window.mammoth);
            if (src.includes('xlsx')) resolve(window.XLSX);
        };
        script.onerror = () => {
            reject(new Error(`加载失败: ${src}`));
        };
        document.head.appendChild(script);
    });
}

function loadMammoth() {
    return loadScript('https://cdn.jsdelivr.net/npm/mammoth@1.6.0/mammoth.browser.min.js');
}

function loadXLSX() {
    return loadScript('https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js');
}

export default {
    name: 'FilePreview',
    props: {
        // 下载基础URL
        downloadBaseUrl: {
            type: String,
            default: '/web/dataComplianceDetail/download'
        },
        // 自定义查询参数
        customParams: {
            type: Object,
            default: () => ({})
        },
        // 控制是否可以下载，默认true（可下载）
        canDownload: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            previewVisible: false,
            previewLoading: false,
            previewUrl: '',
            fileContent: '',
            docxContent: '',
            fileInfo: null,
            registrationId: '',
            previewError: '',

            // 图片加载状态
            imageLoading: false,
            imageLoadError: false,
            imageRetryCount: 0, // 图片重试计数器
            maxImageRetryCount: 3, // 最大重试次数

            // 图片缩放和旋转相关
            scale: 1, // 缩放比例
            rotation: 0, // 旋转角度
            isDragging: false, // 是否正在拖拽
            dragStartX: 0, // 拖拽起始X坐标
            dragStartY: 0, // 拖拽起始Y坐标
            translateX: 0, // X轴平移
            translateY: 0, // Y轴平移
            isFullscreen: false, // 是否全屏

            // PDF加载状态
            pdfLoading: false,
            pdfLoadError: false,

            // Excel相关数据
            excelSheets: [],
            currentSheetIndex: 0,
            currentSheetData: [],
            currentSheetHeaders: [],
            excelLoading: false,
            currentPage: 1,
            pageSize: 100,

            supportedImageTypes: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico'],
            supportedTextTypes: ['txt', 'json', 'xml', 'csv', 'log', 'js', 'css', 'html', 'md'],
        };
    },
    computed: {
        isImage() {
            if (!this.fileInfo) return false;
            const ext = this.getFileExtension(this.fileInfo.fileName);
            return this.supportedImageTypes.includes(ext);
        },
        isPdf() {
            if (!this.fileInfo) return false;
            const ext = this.getFileExtension(this.fileInfo.fileName);
            return ext === 'pdf';
        },
        isExcel() {
            if (!this.fileInfo) return false;
            const ext = this.getFileExtension(this.fileInfo.fileName);
            return ['xls', 'xlsx', 'csv'].includes(ext);
        },
        isDocx() {
            if (!this.fileInfo) return false;
            const ext = this.getFileExtension(this.fileInfo.fileName);
            return ['doc', 'docx'].includes(ext);
        },
        isText() {
            if (!this.fileInfo) return false;
            const ext = this.getFileExtension(this.fileInfo.fileName);
            return this.supportedTextTypes.includes(ext);
        },
        fileType() {
            if (!this.fileInfo) return 'unknown';
            return this.getFileExtension(this.fileInfo.fileName);
        },
        fileName() {
            return this.fileInfo?.fileName || '文件';
        },
        previewTitle() {
            return `预览 - ${this.fileName}`;
        },
        currentSheet() {
            return this.excelSheets[this.currentSheetIndex] || {};
        },
        imageTransform() {
            return {
                transform: `scale(${this.scale}) rotate(${this.rotation}deg) translate(${this.translateX}px, ${this.translateY}px)`,
                cursor: this.isDragging ? 'grabbing' : (this.scale > 1 ? 'grab' : 'default'),
                transformOrigin: 'center center'
            };
        },
    },
    methods: {
        // 获取文件扩展名
        getFileExtension(filename) {
            if (!filename) return '';
            const parts = filename.split('.');
            return parts.length > 1 ? parts.pop().toLowerCase() : '';
        },

        // 根据文件扩展名获取MIME类型
        getMimeTypeByExtension(ext) {
            const mimeTypes = {
                // 图片类型
                'jpg': 'image/jpeg',
                'jpeg': 'image/jpeg',
                'png': 'image/png',
                'gif': 'image/gif',
                'bmp': 'image/bmp',
                'webp': 'image/webp',
                'svg': 'image/svg+xml',
                'ico': 'image/x-icon',

                // PDF类型
                'pdf': 'application/pdf',

                // 其他文档类型
                'doc': 'application/msword',
                'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'xls': 'application/vnd.ms-excel',
                'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'ppt': 'application/vnd.ms-powerpoint',
                'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',

                // 文本类型
                'txt': 'text/plain',
                'json': 'application/json',
                'xml': 'application/xml',
                'csv': 'text/csv',
                'html': 'text/html',
                'css': 'text/css',
                'js': 'application/javascript',
                'md': 'text/markdown'
            };

            return mimeTypes[ext] || 'application/octet-stream';
        },

        getDownloadUrl(fileInfo) {
            const baseParams = {
                registrationId: this.registrationId,
                fileName: fileInfo.fileName
            };

            // 合并基础参数和自定义参数
            const allParams = { ...baseParams, ...this.customParams };

            const params = new URLSearchParams();

            Object.keys(allParams).forEach(key => {
                if (allParams[key] !== undefined && allParams[key] !== null && allParams[key] !== '') {
                    params.append(key, allParams[key]);
                }
            });
            let url = ''
            if (this.downloadBaseUrl == '/web/file/download') {

                url = `${this.downloadBaseUrl}/${fileInfo.fileName}?${params.toString()}`;


            } else {

                url = `${this.downloadBaseUrl}?${params.toString()}`;
            }

            return url;
        },


        // 打开预览
        async openPreview(fileInfo, registrationId, customParams = {}, canDownload = null) {
            this.fileInfo = fileInfo;
            this.registrationId = registrationId;
            this.previewVisible = true;
            this.previewLoading = true;
            this.previewError = '';

            // 如果传入了canDownload参数，使用传入的值，否则使用props的默认值
            if (canDownload !== null) {
                this.$emit('update:canDownload', canDownload);
            }

            // 重置加载状态
            this.imageLoading = false;
            this.imageLoadError = false;
            this.imageRetryCount = 0;
            this.pdfLoading = false;
            this.pdfLoadError = false;

            // 重置缩放和旋转状态
            this.resetZoom();
            this.resetRotation();
            this.resetPosition();

            // 重置Excel数据
            this.excelSheets = [];
            this.currentSheetIndex = 0;
            this.currentSheetData = [];
            this.currentSheetHeaders = [];
            this.currentPage = 1;

            // 如果传入了自定义参数，合并到 this.customParams
            if (customParams && Object.keys(customParams).length > 0) {
                this.customParams = { ...this.customParams, ...customParams };
            }

            try {
                if (!fileInfo || !fileInfo.fileName) {
                    throw new Error('文件信息不完整');
                }

                if (this.isImage) {
                    this.imageLoading = true;
                    await this.previewImage(fileInfo);
                } else if (this.isPdf) {
                    this.pdfLoading = true;
                    await this.previewPdf(fileInfo, canDownload);
                } else if (this.isExcel) {
                    await this.previewExcel(fileInfo);
                } else if (this.isDocx) {
                    await this.previewDocx(fileInfo);
                } else if (this.isText) {
                    await this.previewText(fileInfo);
                } else {
                    throw new Error('不支持的文件类型');
                }
            } catch (error) {
                console.error('预览失败:', error);
                this.handlePreviewError(error);
            } finally {
                this.previewLoading = false;
            }
        },

        // 预览图片
        async previewImage(fileInfo) {
            try {
                const downloadUrl = this.getDownloadUrl(fileInfo);

                const response = await this.$axios({
                    method: 'get',
                    url: downloadUrl,
                    responseType: 'blob',
                    timeout: 30000,
                    headers: {
                        'Accept': 'image/*, application/octet-stream'
                    }
                });

                if (!response.data || response.data.size === 0) {
                    throw new Error('图片内容为空或损坏');
                }
                // 获取文件扩展名
                const fileExt = this.getFileExtension(fileInfo.fileName).toLowerCase();

                // 检查是否支持此图片格式
                if (!this.supportedImageTypes.includes(fileExt)) {
                    throw new Error(`不支持的图片格式: ${fileExt}`);
                }

                // 释放之前的URL
                this.revokeObjectURL(this.previewUrl);

                // 创建对象URL
                const mimeType = this.getMimeTypeByExtension(fileExt);
                const blob = new Blob([response.data], {
                    type: mimeType
                });

                this.previewUrl = URL.createObjectURL(blob);


                // 验证图片是否有效
                await this.validateImage(this.previewUrl);

            } catch (error) {
                console.error('图片预览失败:', error);
                this.imageLoadError = true;
                this.previewError = `图片预览失败: ${error.message}`;
                throw error;
            } finally {
                this.imageLoading = false;
            }
        },

        // 验证图片是否有效
        validateImage(imageUrl) {
            return new Promise((resolve, reject) => {
                const img = new Image();
                let isLoaded = false;
                let isError = false;

                // 设置超时
                const timeoutId = setTimeout(() => {
                    if (!isLoaded && !isError) {
                        img.onload = null;
                        img.onerror = null;
                        reject(new Error('图片加载超时'));
                    }
                }, 30000);

                img.onload = () => {
                    clearTimeout(timeoutId);
                    isLoaded = true;


                    // 检查图片尺寸
                    if (img.naturalWidth === 0 || img.naturalHeight === 0) {
                        console.warn('图片尺寸为0，可能无效');
                        reject(new Error('图片尺寸异常'));
                    } else {
                        resolve(true);
                    }
                };

                img.onerror = (err) => {
                    clearTimeout(timeoutId);
                    isError = true;
                    console.error('图片加载失败:', err, 'URL:', imageUrl);
                    reject(new Error('图片加载失败，文件可能已损坏或格式不支持'));
                };

                // 设置crossOrigin属性，处理跨域问题
                img.crossOrigin = 'anonymous';
                img.src = imageUrl;

                // 如果图片已经在缓存中，立即触发onload
                if (img.complete && img.naturalWidth !== 0 && img.naturalHeight !== 0) {
                    img.onload();
                } else if (img.complete) {
                    // 图片已完成加载，但尺寸为0
                    console.warn('图片加载完成但尺寸为0');
                    reject(new Error('图片加载完成但尺寸为0，可能文件已损坏'));
                }
            });
        },

        // 预览PDF
        async previewPdf(fileInfo, canDownload) {
            try {
                const downloadUrl = this.getDownloadUrl(fileInfo);
                console.log('PDF下载URL:', downloadUrl);

                const response = await this.$axios({
                    method: 'get',
                    url: downloadUrl,
                    responseType: 'blob',
                    timeout: 30000
                });

                if (!response.data || response.data.size === 0) {
                    throw new Error('PDF文件内容为空');
                }


                // 释放之前的URL
                this.revokeObjectURL(this.previewUrl);

                // 创建对象URL
                const blob = new Blob([response.data], {
                    type: 'application/pdf'
                });
                this.previewUrl = URL.createObjectURL(blob);


                if (!canDownload) {
                    // 创建对象URL并添加参数隐藏工具栏
                    const objectUrl = URL.createObjectURL(blob);
                    this.previewUrl = `${objectUrl}#toolbar=0`;
                }

            } catch (error) {
                console.error('PDF预览失败:', error);
                this.pdfLoadError = true;
                this.previewError = `PDF预览失败: ${error.message}`;
                throw error;
            } finally {
                this.pdfLoading = false;
            }
        },

        // 图片缩放功能
        zoomIn() {
            if (this.scale < 3) {
                this.scale = Math.min(this.scale + 0.1, 3);
            }
        },

        zoomOut() {
            if (this.scale > 0.2) {
                this.scale = Math.max(this.scale - 0.1, 0.2);
            }
        },

        resetZoom() {
            this.scale = 1;
            this.resetPosition();
        },

        // 图片旋转功能
        rotateLeft() {
            this.rotation = (this.rotation - 90) % 360;
        },

        rotateRight() {
            this.rotation = (this.rotation + 90) % 360;
        },

        resetRotation() {
            this.rotation = 0;
            this.scale = 1
        },

        // 重置位置
        resetPosition() {
            this.translateX = 0;
            this.translateY = 0;
        },

        // 全屏功能
        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen;
        },

        // 鼠标滚轮缩放
        handleWheel(event) {
            event.preventDefault();
            const delta = event.deltaY > 0 ? -0.1 : 0.1;
            const newScale = Math.max(0.2, Math.min(3, this.scale + delta));
            this.scale = newScale;
        },

        // 拖拽功能
        startDrag(event) {
            if (this.scale <= 1) return;

            this.isDragging = true;
            this.dragStartX = event.clientX - this.translateX;
            this.dragStartY = event.clientY - this.translateY;
        },

        handleDrag(event) {
            if (!this.isDragging) return;

            event.preventDefault();
            this.translateX = event.clientX - this.dragStartX;
            this.translateY = event.clientY - this.dragStartY;
        },

        endDrag() {
            this.isDragging = false;
        },

        // 处理图片加载错误
        handleImageError(event) {
            console.error('图片加载失败事件触发:', event);
            console.log('当前图片URL:', this.previewUrl);
            console.log('图片加载状态:', {
                imageLoading: this.imageLoading,
                imageLoadError: this.imageLoadError,
                retryCount: this.imageRetryCount
            });

            this.imageLoadError = true;
            this.imageLoading = false;

            // 重试机制
            if (this.imageRetryCount < this.maxImageRetryCount) {
                this.imageRetryCount++;
                console.log(`图片加载失败，重试第${this.imageRetryCount}次`);

                // 清除当前URL
                this.revokeObjectURL(this.previewUrl);
                this.previewUrl = '';

                // 延迟重试
                setTimeout(() => {
                    if (this.fileInfo) {
                        this.retryImagePreview();
                    }
                }, 1000);

                return;
            }

            this.previewError = '图片加载失败，请检查文件格式或重试';

            this.$message.error('图片加载失败');
        },

        // 重试图片预览
        async retryImagePreview() {
            if (!this.fileInfo) return;

            try {
                this.imageLoading = true;
                this.imageLoadError = false;
                this.previewError = '';

                await this.previewImage(this.fileInfo);
            } catch (error) {
                console.error('图片重试预览失败:', error);
                this.imageLoadError = true;
                this.imageLoading = false;
                this.previewError = '图片预览失败，请检查文件格式或重试';
            }
        },

        // 处理图片加载成功
        handleImageLoad(event) {


            this.imageLoading = false;
            this.imageLoadError = false;
            this.previewError = '';
            this.imageRetryCount = 0; // 重置重试计数器

        },

        // 处理PDF加载错误
        handlePdfError(event) {
            console.error('PDF加载失败:', event);
            this.pdfLoadError = true;
            this.pdfLoading = false;
            this.previewError = 'PDF加载失败，请检查文件格式或重试';

            this.revokeObjectURL(this.previewUrl);
            this.previewUrl = '';

            this.$message.error('PDF加载失败');
        },

        // 处理PDF加载成功
        handlePdfLoad() {
            console.log('PDF加载成功');
            this.pdfLoading = false;
            this.pdfLoadError = false;
            this.previewError = '';
        },

        // 预览Excel文件
        async previewExcel(fileInfo) {
            const downloadUrl = this.getDownloadUrl(fileInfo);
            let response;
            try {
                this.excelLoading = true;

                response = await this.$axios({
                    method: 'get',
                    url: downloadUrl,
                    responseType: 'arraybuffer',
                    timeout: 30000
                });

                if (!response.data || response.data.byteLength === 0) {
                    throw new Error('文件内容为空或损坏');
                }

                const XLSX = await loadXLSX();

                // 读取Excel文件
                const workbook = XLSX.read(response.data, { type: 'array' });

                // 处理工作表 - 添加数据验证
                this.excelSheets = workbook.SheetNames.map((name, index) => {
                    const worksheet = workbook.Sheets[name];

                    // 使用header: 1来获取原始数组数据
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                        header: 1,
                        defval: '' // 为undefined的值提供默认值
                    });

                    // 清理数据：移除完全为空的行
                    const cleanedData = jsonData.filter(row => {
                        // 检查行是否有任何非空值
                        return row && row.some(cell =>
                            cell !== undefined &&
                            cell !== null &&
                            String(cell).trim() !== ''
                        );
                    });

                    return {
                        name: name,
                        index: index,
                        data: cleanedData,
                        rowCount: cleanedData.length
                    };
                });

                if (this.excelSheets.length === 0) {
                    throw new Error('Excel文件中没有找到工作表');
                }

                // 显示第一个工作表
                this.switchSheet(0);

            } catch (error) {
                this.previewError = 'Excel文件预览失败: ' + error.message;
                this.$message.error('Excel预览失败');
                throw error;
            } finally {
                this.excelLoading = false;
            }
        },
        // 切换工作表
        switchSheet(sheetIndex) {
            if (sheetIndex < 0 || sheetIndex >= this.excelSheets.length) {
                return;
            }

            this.currentSheetIndex = sheetIndex;
            this.currentPage = 1;

            const sheet = this.excelSheets[sheetIndex];
            const sheetData = sheet.data;

            if (sheetData.length === 0) {
                this.currentSheetData = [];
                this.currentSheetHeaders = [];
                return;
            }

            // 处理表头 - 确保所有表头元素都有有效的值
            const headers = sheetData[0] || [];
            this.currentSheetHeaders = headers.map((header, index) => {
                // 确保header不是undefined或null
                const title = header !== undefined && header !== null ?
                    String(header).trim() || `列${index + 1}` :
                    `列${index + 1}`;

                return {
                    key: `col${index}`,
                    title: title
                };
            });

            // 处理数据行（跳过表头）
            const dataRows = sheetData.slice(1);
            this.currentSheetData = dataRows.map((row, rowIndex) => {
                const rowData = { id: rowIndex };

                // 确保每行都有与表头对应的列
                this.currentSheetHeaders.forEach((header, cellIndex) => {
                    rowData[header.key] = row && row[cellIndex] !== undefined ?
                        row[cellIndex] :
                        '';
                });

                return rowData;
            });
        },

        // 处理分页
        handlePageChange(page) {
            this.currentPage = page;
        },

        // 预览DOCX文档
        async previewDocx(fileInfo) {
            const downloadUrl = this.getDownloadUrl(fileInfo);
            let response;
            try {
                response = await this.$axios({
                    method: 'get',
                    url: downloadUrl,
                    responseType: 'arraybuffer',
                    timeout: 30000
                });

                if (!response.data || response.data.byteLength === 0) {
                    throw new Error('文件内容为空或损坏');
                }

                await this.previewWithMammoth(response.data);

            } catch (error) {
                console.error('DOCX预览失败:', error);
                if (response && response.data) {
                    await this.previewDocxSimple(response.data);
                } else {
                    this.previewError = '文件下载失败或内容为空';
                    this.$message.error('DOCX预览失败');
                }
            }
        },

        // 使用mammoth预览DOCX
        async previewWithMammoth(arrayBuffer) {
            try {
                const mammoth = await loadMammoth();
                const result = await mammoth.convertToHtml({ arrayBuffer });

                if (result.value) {
                    this.docxContent = this.formatDocxHtml(result.value);
                } else {
                    throw new Error('文档内容为空');
                }
            } catch (error) {
                throw new Error('文档转换失败: ' + error.message);
            }
        },

        // 简单的DOCX文本预览
        async previewDocxSimple(arrayBuffer) {
            try {
                const text = this.extractSimpleText(arrayBuffer);
                this.docxContent = this.formatSimpleText(text, '这是Word文档的简化预览，部分格式可能无法显示。');
            } catch (error) {
                throw new Error('文档预览失败: ' + error.message);
            }
        },

        // 从DOCX中提取简单文本
        extractSimpleText(arrayBuffer) {
            try {
                const decoder = new TextDecoder('utf-8');
                let text = decoder.decode(arrayBuffer);

                const textMatches = text.match(/>(.*?)</g);
                if (textMatches) {
                    const extractedText = textMatches.map(match => match.slice(1, -1))
                        .join(' ')
                        .replace(/\s+/g, ' ')
                        .trim();
                    if (extractedText.length > 0) {
                        return extractedText;
                    }
                }

                const printableText = text.replace(/[^\x20-\x7E\n\r\t]/g, '')
                    .replace(/\s+/g, ' ')
                    .trim();

                return printableText || '无法提取文档内容，文件可能已损坏或格式不支持';
            } catch (error) {
                return '文件内容提取失败: ' + error.message;
            }
        },

        // 预览文本文件
        async previewText(fileInfo) {
            const downloadUrl = this.getDownloadUrl(fileInfo);
            const response = await this.$axios({
                method: 'get',
                url: downloadUrl,
                responseType: 'blob',
                timeout: 30000
            });

            if (response.data.size === 0) {
                throw new Error('文件内容为空');
            }

            this.fileContent = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => reject(new Error('文件读取失败'));
                reader.readAsText(new Blob([response.data]));
            });
        },

        // 格式化DOCX转换后的HTML
        formatDocxHtml(html) {
            return `
        <div class="docx-html-content">
          <style>
            .docx-html-content {
              font-family: "Microsoft YaHei", "Segoe UI", sans-serif;
              line-height: 1.6;
              color: #333;
              padding: 20px;
              max-height: 600px;
              overflow-y: auto;
            }
            .docx-html-content h1 { font-size: 24px; margin: 20px 0 15px 0; color: #2c3e50; }
            .docx-html-content h2 { font-size: 20px; margin: 18px 0 12px 0; color: #34495e; }
            .docx-html-content h3 { font-size: 16px; margin: 15px 0 10px 0; color: #2c3e50; }
            .docx-html-content p { margin: 12px 0; }
            .docx-html-content table { border-collapse: collapse; width: 100%; margin: 15px 0; }
            .docx-html-content table, .docx-html-content th, .docx-html-content td { border: 1px solid #ddd; }
            .docx-html-content th, .docx-html-content td { padding: 8px 12px; }
            .docx-html-content th { background-color: #f5f5f5; }
            .docx-html-content ul, .docx-html-content ol { margin: 12px 0; padding-left: 30px; }
            .docx-html-content li { margin: 6px 0; }
          </style>
          ${html || '<p>文档内容为空</p>'}
        </div>
      `;
        },

        // 格式化简单文本
        formatSimpleText(text, notice = '') {
            return `
        <div class="simple-text-content">
          <style>
            .simple-text-content {
              font-family: "Microsoft YaHei", sans-serif;
              line-height: 1.8;
              color: #333;
              padding: 20px;
              max-height: 600px;
              overflow-y: auto;
              white-space: pre-wrap;
              background: #f9f9f9;
              border-radius: 4px;
            }
            .text-notice {
              margin-top: 20px;
              padding: 10px;
              background: #fff3cd;
              border-left: 4px solid #ffc107;
              font-size: 14px;
            }
          </style>
          <div>${text}</div>
          ${notice ? `<div class="text-notice">${notice}</div>` : ''}
        </div>
      `;
        },

        // 处理预览错误
        handlePreviewError(error) {
            console.error('预览错误:', error);

            let errorMessage = error.message || '文件预览失败';

            // 针对常见错误提供友好提示
            if (errorMessage.includes('network') || errorMessage.includes('Network')) {
                errorMessage = '网络连接失败，请检查网络设置';
            } else if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
                errorMessage = '登录已过期，请重新登录';
            } else if (errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
                errorMessage = '没有文件访问权限';
            } else if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
                errorMessage = '文件不存在或已被删除';
            } else if (errorMessage.includes('HTML')) {
                errorMessage = '服务器返回了错误的响应，请检查登录状态';
            } else if (errorMessage.includes('timeout')) {
                errorMessage = '请求超时，请稍后重试';
            } else if (errorMessage.includes('base64')) {
                errorMessage = '文件编码格式不支持预览';
            }

            this.previewError = errorMessage;
            this.$message.error(errorMessage);
        },

        // 重试预览
        async retryPreview() {
            if (this.fileInfo) {
                this.previewError = '';
                await this.openPreview(this.fileInfo, this.registrationId, {},
                    null);
            }
        },

        // 下载文件
        async handleDownload() {
            if (!this.fileInfo) {
                this.$message.error('没有可下载的文件');
                return;
            }

            const downloadUrl = this.getDownloadUrl(this.fileInfo);

            try {
                const response = await this.$axios({
                    method: 'get',
                    url: downloadUrl,
                    responseType: 'blob',
                    timeout: 30000
                });

                if (!response.data || response.data.size === 0) {
                    this.$message.error('文件内容为空，无法下载');
                    return;
                }

                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', this.fileInfo.fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                this.$message.success('文件下载开始');
            } catch (error) {
                console.error('文件下载失败:', error);
                this.$message.error('文件下载失败: ' + (error.message || ''));
            }
        },

        // 安全释放URL对象
        revokeObjectURL(url) {
            if (url && url.startsWith('blob:')) {
                try {
                    URL.revokeObjectURL(url);
                } catch (e) {
                    console.warn('释放URL失败:', e);
                }
            }
        },

        // 关闭对话框
        handleClose() {
            this.revokeObjectURL(this.previewUrl);
            this.previewUrl = '';
            this.fileContent = '';
            this.docxContent = '';
            this.fileInfo = null;
            this.registrationId = '';
            this.previewError = '';

            // 重置加载状态
            this.imageLoading = false;
            this.imageLoadError = false;
            this.imageRetryCount = 0;
            this.pdfLoading = false;
            this.pdfLoadError = false;

            // 重置图片状态
            this.scale = 1;
            this.rotation = 0;
            this.translateX = 0;
            this.translateY = 0;
            this.isDragging = false;
            this.isFullscreen = false;

            // 清理Excel数据
            this.excelSheets = [];
            this.currentSheetData = [];
            this.currentSheetHeaders = [];

        },
    },
};
</script>
<style scoped>


.preview-container {
    min-height: 600px;
    display: flex;
    flex-direction: column;
    user-select: none;

}

/* 图片预览样式 */
.image-preview {
    width: 100%;
    height: 75vh;
    min-height: 600px;
    display: flex;
    flex-direction: column;
    background: #f8f9fa;
    border-radius: 8px;
    overflow: hidden;
    position: relative;
}

.image-content {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
}

/* 图片工具栏 */
.image-toolbar {
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    background: rgba(255, 255, 255, 0.95);
    padding: 8px 12px;
    border-radius: 6px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid #e0e0e0;
}

.image-toolbar .el-button-group {
    display: flex;
    gap: 4px;
}

.image-toolbar .el-button--mini {
    padding: 6px 10px;
    font-size: 12px;
    border-radius: 4px;
}

.image-toolbar .el-button--mini i {
    margin-right: 4px;
}

/* 图片容器 */
.image-container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    position: relative;
    cursor: default;
}

.image-container.fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
    background: #000;
}

/* 可缩放图片 */
.scalable-image {
    max-width: none;
    max-height: none;
    transition: transform 0.2s ease;
    user-select: none;
    -webkit-user-drag: none;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    background: white;
}

/* 加载状态样式 */
.image-loading {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    color: #909399;
    background: #f8f9fa;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 10;
}

.image-loading i {
    font-size: 48px;
    margin-bottom: 16px;
    color: #409EFF;
    animation: rotate 2s linear infinite;
}

.image-loading p {
    font-size: 16px;
    color: #606266;
    margin: 0;
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

/* 错误状态样式 */
.image-error {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 40px 30px;
    background: #fef0f0;
}

.image-error ul {
    text-align: left;
    margin: 20px 0;
    display: inline-block;
}

.image-error li {
    margin: 8px 0;
    list-style-type: disc;
    line-height: 1.6;
    color: #f56c6c;
}

.error-action {
    margin-top: 25px;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: center;
}

/* PDF预览样式 */
.pdf-preview {
    width: 100%;
    height: 75vh;
    min-height: 600px;
    display: flex;
    flex-direction: column;
    background: #f8f9fa;
    border-radius: 8px;
    overflow: hidden;
}

.pdf-content {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.pdf-iframe-container {
    flex: 1;
    width: 100%;
    height: 100%;
    background: white;
    position: relative;
}

.pdf-viewer {
    width: 100%;
    height: 100%;
    border: none;
    background: white;

}

.pdf-loading {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    color: #909399;
    background: #f8f9fa;
}

.pdf-loading i {
    font-size: 48px;
    margin-bottom: 16px;
    color: #409EFF;
    animation: rotate 2s linear infinite;
}

.pdf-loading p {
    font-size: 16px;
    color: #606266;
    margin: 0;
}

.pdf-error {
    padding: 40px 30px;
    background: #fef0f0;
    border-radius: 8px;
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
}

.pdf-error ul {
    text-align: left;
    margin: 20px 0;
    display: inline-block;
}

.pdf-error li {
    margin: 8px 0;
    list-style-type: disc;
    line-height: 1.6;
    color: #f56c6c;
}

/* Excel预览样式 */
.excel-preview {
    width: 100%;
    height: 75vh;
    min-height: 600px;
    display: flex;
    flex-direction: column;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
}

.excel-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
    flex-shrink: 0;
}

.excel-info {
    font-size: 14px;
    color: #666;
    font-weight: 500;
}

.excel-table-container {
    flex: 1;
    overflow: auto;
    background: #fff;
}

.excel-pagination {
    padding: 12px 16px;
    border-top: 1px solid #e0e0e0;
    background: #f5f5f5;
    display: flex;
    justify-content: center;
}

/* DOCX预览样式 */
.docx-preview {
    width: 100%;
    height: 75vh;
    min-height: 600px;
    overflow: auto;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
}

.docx-content {
    padding: 30px;
    background: #fff;
    min-height: 100%;
    box-sizing: border-box;
}

.docx-html-content {
    font-family: "Microsoft YaHei", "Segoe UI", sans-serif;
    line-height: 1.6;
    color: #333;
    padding: 20px;
    max-height: 600px;
    overflow-y: auto;
}

.docx-html-content h1 {
    font-size: 24px;
    margin: 20px 0 15px 0;
    color: #2c3e50;
    border-bottom: 2px solid #409EFF;
    padding-bottom: 8px;
}

.docx-html-content h2 {
    font-size: 20px;
    margin: 18px 0 12px 0;
    color: #34495e;
}

.docx-html-content h3 {
    font-size: 16px;
    margin: 15px 0 10px 0;
    color: #2c3e50;
}

.docx-html-content p {
    margin: 12px 0;
    line-height: 1.8;
}

.docx-html-content table {
    border-collapse: collapse;
    width: 100%;
    margin: 15px 0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.docx-html-content table,
.docx-html-content th,
.docx-html-content td {
    border: 1px solid #ddd;
}

.docx-html-content th,
.docx-html-content td {
    padding: 10px 12px;
    text-align: left;
}

.docx-html-content th {
    background-color: #f5f5f5;
    font-weight: 600;
    color: #333;
}

.docx-html-content ul,
.docx-html-content ol {
    margin: 12px 0;
    padding-left: 30px;
}

.docx-html-content li {
    margin: 6px 0;
    line-height: 1.6;
}

/* 文本预览样式 */
.text-preview {
    width: 100%;
    height: 75vh;
    min-height: 600px;
    overflow: auto;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
}

.text-preview pre {
    white-space: pre-wrap;
    word-wrap: break-word;
    font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
    font-size: 14px;
    line-height: 1.6;
    margin: 0;
    padding: 24px;
    background: transparent;
    color: #333;
    min-height: 100%;
    box-sizing: border-box;
}

/* 不支持预览的文件类型样式 */
.unsupported-preview {
    text-align: center;
    padding: 50px 30px;
    height: 75vh;
    min-height: 600px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background: #f8f9fa;
    border-radius: 8px;
}

.download-action {
    margin-top: 30px;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: center;
}

/* 预览错误样式 */
.preview-error {
    text-align: center;
    padding: 50px 30px;
    height: 75vh;
    min-height: 600px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background: #f8f9fa;
    border-radius: 8px;
}

/* 对话框底部样式 */
.dialog-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 20px 0 0 0;
    border-top: 1px solid #e0e0e0;
    margin-top: 20px;
}

/* 按钮样式优化 */
.el-button {
    border-radius: 6px;
    font-weight: 500;
    transition: all 0.3s ease;
}

.el-button--primary {
    background: #409EFF;
    border-color: #409EFF;
}

.el-button--primary:hover {
    background: #66b1ff;
    border-color: #66b1ff;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
}

.el-button--primary:active {
    transform: translateY(0);
}

.el-button--mini {
    padding: 6px 12px;
    font-size: 12px;
}

.el-button--small {
    padding: 8px 15px;
    font-size: 12px;
}

.el-button--medium {
    padding: 10px 20px;
    font-size: 14px;
}

/* 响应式设计 */
@media (max-height: 800px) {

    .image-preview,
    .pdf-preview,
    .excel-preview,
    .docx-preview,
    .text-preview,
    .unsupported-preview,
    .preview-error {
        height: 70vh;
        min-height: 500px;
    }

    .image-toolbar {
        top: 5px;
        padding: 6px 10px;
    }
}

@media (max-height: 600px) {

    .image-preview,
    .pdf-preview,
    .excel-preview,
    .docx-preview,
    .text-preview,
    .unsupported-preview,
    .preview-error {
        height: 65vh;
        min-height: 450px;
    }

    .image-toolbar {
        top: 2px;
        padding: 4px 8px;
    }

    .image-toolbar .el-button--mini {
        padding: 4px 8px;
        font-size: 11px;
    }
}

@media (max-width: 768px) {
    .image-toolbar {
        left: 10px;
        right: 10px;
        transform: none;
        width: auto;
    }

    .image-toolbar .el-button-group {
        flex-wrap: wrap;
        justify-content: center;
    }

    .excel-toolbar {
        flex-direction: column;
        gap: 10px;
        align-items: stretch;
    }

    .excel-info {
        text-align: center;
    }
}

/* 全屏模式下的特殊样式 */
.image-container.fullscreen .scalable-image {
    max-width: 95vw;
    max-height: 95vh;
    transition: transform 0.3s ease;
}

.image-container.fullscreen .image-toolbar {
    background: rgba(0, 0, 0, 0.8);
    border: 1px solid #555;
}

.image-container.fullscreen .image-toolbar .el-button {
    background: rgba(255, 255, 255, 0.1);
    border-color: #666;
    color: white;
}

.image-container.fullscreen .image-toolbar .el-button:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: #888;
}

.image-container.fullscreen .image-toolbar .el-button:disabled {
    background: rgba(255, 255, 255, 0.05);
    border-color: #444;
    color: #999;
}

/* 滚动条样式优化 */
::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}

::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

/* 确保对话框正确显示 */
:deep(.el-dialog__body) {
    padding: 20px;
    max-height: 80vh;
    overflow-y: auto;
}

:deep(.el-dialog) {
    display: flex;
    flex-direction: column;
    max-height: 90vh;
}

:deep(.el-dialog__wrapper) {
    display: flex;
    justify-content: center;
    align-items: center;
}

:deep(.el-loading-mask) {
    border-radius: 8px;
}

/* 动画效果 */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.scale-enter-active,
.scale-leave-active {
    transition: all 0.3s ease;
}

.scale-enter,
.scale-leave-to {
    opacity: 0;
    transform: scale(0.9);
}

/* 工具提示样式优化 */
:deep(.el-tooltip__popper) {
    max-width: 300px;
    line-height: 1.6;
}

/* 表格样式优化 */
:deep(.el-table) {
    border-radius: 8px;
    overflow: hidden;
}

:deep(.el-table__header) {
    background: #f5f7fa;
}

:deep(.el-table th) {
    background: #f5f7fa;
    color: #606266;
    font-weight: 600;
}

:deep(.el-table--striped .el-table__body tr.el-table__row--striped td) {
    background: #fafafa;
}

:deep(.el-table--enable-row-hover .el-table__body tr:hover>td) {
    background-color: #f5f7fa;
}

/* 分页样式优化 */
:deep(.el-pagination) {
    padding: 10px 0;
    justify-content: center;
}

:deep(.el-pagination .btn-prev),
:deep(.el-pagination .btn-next) {
    border-radius: 4px;
}

:deep(.el-pagination .number) {
    border-radius: 4px;
}

:deep(.el-pagination .active) {
    background: #409EFF;
    color: white;
}

/* 警告提示样式优化 */
:deep(.el-alert) {
    border-radius: 8px;
    margin-bottom: 20px;
}

:deep(.el-alert__title) {
    font-weight: 600;
    font-size: 14px;
}

:deep(.el-alert--error) {
    background-color: #fef0f0;
    border: 1px solid #fbc4c4;
}

:deep(.el-alert--warning) {
    background-color: #fdf6ec;
    border: 1px solid #f5dab1;
}

:deep(.el-alert--success) {
    background-color: #f0f9eb;
    border: 1px solid #e1f3d8;
}

:deep(.el-alert--info) {
    background-color: #f4f4f5;
    border: 1px solid #e9e9eb;
}
</style>
